import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ReclassLocalChartService {

  constructor(private http: HttpClient) {}

  getCodeCombination(baseUrl: string, combinationKey: number): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_id', '9');
    urlParams.set('combination_key', combinationKey.toString());

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject?.[0] || null;
      })
    );
  }

  getAccounts(
    baseUrl: string,
    params: {
      screen_key: number;
      combination_key: number;
      ge_gecs_entity: string;
      chart_key: number;
      parent_system_acct_key: number;
      source_type: string;
      search_type: string;
      search_string: string;
      acct_type_str: string;
      system_acct_search: string;
    },
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'unmge8');
    
    Object.keys(params).forEach(key => {
      if ((params as any)[key] !== undefined && (params as any)[key] !== null) {
        urlParams.set(key, (params as any)[key].toString());
      }
    });

    // Add global params
    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Get trading partners for FIR
  getTradingPartnersFir(
    baseUrl: string,
    taxYear: string,
    scenario: string,
    searchString: string,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', '50swfi');
    urlParams.set('tax_year', taxYear || '');
    urlParams.set('scenario', scenario || '');
    urlParams.set('search_string', searchString || '');

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Get trading partners for DCS
  getTradingPartnersDcs(
    baseUrl: string,
    taxYear: string,
    scenario: string,
    searchString: string,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'sckfcq');
    urlParams.set('tax_year', taxYear);
    urlParams.set('scenario', scenario);
    urlParams.set('search_string', searchString);

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Get amounts for details adjustment
  getAmountsForDtlsAdj(
    baseUrl: string,
    combinationKey: number,
    accountKey: number,
    chartKey: number,
    tpKey: number,
    adjSource: string,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'zf0obh');
    urlParams.set('combination_key', combinationKey.toString());
    urlParams.set('account_key', accountKey.toString());
    urlParams.set('chart_key', chartKey.toString());
    urlParams.set('tp_key', tpKey.toString());
    urlParams.set('adj_source', adjSource);

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Load existing reclasses
  loadExistingReclass(
    baseUrl: string,
    screenKey: number,
    combinationKey: number,
    chartKey: number,
    accountKey: number,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'clgdjl');
    urlParams.set('screen_key', screenKey.toString());
    urlParams.set('combination_key', combinationKey.toString());
    urlParams.set('chart_key', chartKey.toString());
    urlParams.set('account_key', accountKey.toString());

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Save reclass
  saveReclass(
    baseUrl: string,
    settings: any[],
    accounts: any[],
    globalService: any,
    filterParams: any
  ): Observable<any> {
    const jsonObj = JSON.stringify(accounts);
    const jsonSettings = JSON.stringify(settings);

    let params: any = { ...filterParams };
    if (globalService?.globalParams) {
      params = { ...globalService.globalParams, ...params };
    }
    params.jsonObj = jsonObj;
    params.jsonSettings = jsonSettings;
    params.process_name = 'RECLASS_SAVE_LOCAL_ACCT';

    const urlParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlParams.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/saveJsonObject?action_code=xqtzyo',
      urlParams.toString(),
      { headers, withCredentials: true }
    );
  }

  // Delete reclass
  deleteReclass(
    baseUrl: string,
    headerData: any,
    deleteObj: any[],
    globalService: any,
    filterParams: any
  ): Observable<any> {
    const jsonSettings = JSON.stringify([{ tax_year: filterParams.tax_year }]);
    const jsonObj = JSON.stringify(deleteObj);

    let params: any = { ...filterParams };
    if (globalService?.globalParams) {
      params = { ...globalService.globalParams, ...params };
    }
    params = { ...headerData, ...params };
    params.jsonSettings = jsonSettings;
    params.jsonObj = jsonObj;

    const urlParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlParams.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/saveJsonObject?action_code=aemd60',
      urlParams.toString(),
      { headers, withCredentials: true }
    );
  }

  // Get standard reclass descriptions (DCS)
  getStdReclassDescriptions(
    baseUrl: string,
    taxYear: string,
    scenario: string,
    jcdKey: string,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'akjjtr');
    urlParams.set('tax_year', taxYear);
    urlParams.set('scenario', scenario);
    urlParams.set('jcd_key', jcdKey);

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Get standard reclass comments (DCS)
  getStdReclassComments(
    baseUrl: string,
    taxYear: string,
    scenario: string,
    jcdKey: string,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'jo8asp');
    urlParams.set('tax_year', taxYear);
    urlParams.set('scenario', scenario);
    urlParams.set('jcd_key', jcdKey);

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  // Get book amount and adjustment by trading partner
  getBookAmtByTp(
    baseUrl: string,
    screenKey: number,
    combinationKey: number,
    chartKey: number,
    sourceType: string,
    searchType: string,
    searchString: string,
    globalService: any
  ): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'unmge8');
    urlParams.set('screen_key', screenKey.toString());
    urlParams.set('combination_key', combinationKey.toString());
    urlParams.set('chart_key', chartKey.toString());
    urlParams.set('source_type', sourceType);
    urlParams.set('search_type', searchType);
    urlParams.set('search_string', searchString);

    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http.post<any>(
      baseUrl + '/loadJsonObject',
      urlParams.toString(),
      { headers, withCredentials: true }
    ).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  loadStdReclassDesc(baseUrl: string, globalService: any): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'akjjtr');
    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    return this.http.post<any>(baseUrl + '/loadJsonObject', urlParams.toString(), { headers, withCredentials: true }).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }

  loadStdReclassComments(baseUrl: string, globalService: any): Observable<any> {
    const urlParams = new URLSearchParams();
    urlParams.set('action_code', 'jo8asp');
    if (globalService?.globalParams) {
      Object.keys(globalService.globalParams).forEach(key => {
        if (!urlParams.has(key)) {
          urlParams.set(key, globalService.globalParams[key]?.toString() || '');
        }
      });
    }
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    return this.http.post<any>(baseUrl + '/loadJsonObject', urlParams.toString(), { headers, withCredentials: true }).pipe(
      map(response => {
        if (response.errorMessage && response.errorMessage !== 'null') {
          throw new Error(response.errorMessage);
        }
        return response.jsonObject || [];
      })
    );
  }
}
